/**
 * @file services/api/permissionService.ts
 * @brief Permission management service for dynamic RBAC
 */

import apiClient from './apiClient';
import { API_ENDPOINTS } from './endpoints';

function unwrapResponse(response: any) {
  if (response?.data?.success === true && 'data' in response.data) {
    return response.data.data;
  }
  if (response?.data) {
    return response.data;
  }
  return response;
}

export interface PagePermission {
  PERMISSION_ID: number;
  ROLE: string;
  PAGE_KEY: string;
  CAN_ACCESS: boolean;
  CREATED_AT: string;
  UPDATED_AT: string;
}

export interface FeaturePermission {
  PERMISSION_ID: number;
  ROLE: string;
  PAGE_KEY: string;
  FEATURE_KEY: string;
  ACTION: string;
  GRANTED: boolean;
  CREATED_AT: string;
  UPDATED_AT: string;
}

export interface PermissionLog {
  LOG_ID: number;
  CHANGED_BY: string;
  CHANGE_TYPE: 'page_access' | 'feature_permission';
  ROLE: string;
  RESOURCE_KEY: string;
  OLD_VALUE: boolean | null;
  NEW_VALUE: boolean;
  CHANGE_DESCRIPTION: string | null;
  CREATED_AT: string;
}

export interface PermissionStructure {
  pageKey: string;
  pageName: string;
  features: {
    featureKey: string;
    featureName: string;
    actions: string[];
  }[];
}

export const permissionService = {
  /**
   * Get permission structure (pages and features)
   * Super Admin only
   */
  async getStructure(): Promise<PermissionStructure[]> {
    const response = await apiClient.get(API_ENDPOINTS.PERMISSIONS.STRUCTURE);
    return unwrapResponse(response) as PermissionStructure[];
  },

  /**
   * Get all page permissions (matrix view)
   * Super Admin only
   */
  async getAllPagePermissions(): Promise<PagePermission[]> {
    const response = await apiClient.get(API_ENDPOINTS.PERMISSIONS.PAGES);
    return unwrapResponse(response) as PagePermission[];
  },

  /**
   * Get page permissions for current user's role
   */
  async getMyPagePermissions(): Promise<PagePermission[]> {
    const response = await apiClient.get(API_ENDPOINTS.PERMISSIONS.PAGES_ME);
    return unwrapResponse(response) as PagePermission[];
  },

  /**
   * Check if current user can access a specific page
   */
  async checkPageAccess(pageKey: string): Promise<boolean> {
    const response = await apiClient.get(API_ENDPOINTS.PERMISSIONS.PAGE_CHECK(pageKey));
    const data = unwrapResponse(response);
    return data?.canAccess ?? false;
  },

  /**
   * Update page access for a role (bulk)
   * Super Admin only
   */
  async updatePageAccess(
    role: string,
    updates: { pageKey: string; canAccess: boolean }[]
  ): Promise<void> {
    await apiClient.put(API_ENDPOINTS.PERMISSIONS.UPDATE_PAGE(role), { updates });
  },

  /**
   * Get all feature permissions
   * Super Admin only
   */
  async getAllFeaturePermissions(): Promise<FeaturePermission[]> {
    const response = await apiClient.get(API_ENDPOINTS.PERMISSIONS.FEATURES);
    return unwrapResponse(response) as FeaturePermission[];
  },

  /**
   * Get feature permissions for current user's role
   */
  async getMyFeaturePermissions(): Promise<FeaturePermission[]> {
    const response = await apiClient.get(API_ENDPOINTS.PERMISSIONS.FEATURES_ME);
    return unwrapResponse(response) as FeaturePermission[];
  },

  /**
   * Get feature permissions for a specific page
   * Super Admin only
   */
  async getFeaturePermissionsByPage(pageKey: string): Promise<FeaturePermission[]> {
    const response = await apiClient.get(API_ENDPOINTS.PERMISSIONS.FEATURES_BY_PAGE(pageKey));
    return unwrapResponse(response) as FeaturePermission[];
  },

  /**
   * Check if current user has permission for a feature action
   */
  async checkFeaturePermission(
    pageKey: string,
    featureKey: string,
    action: string
  ): Promise<boolean> {
    const response = await apiClient.get(
      API_ENDPOINTS.PERMISSIONS.FEATURE_CHECK(pageKey, featureKey, action)
    );
    const data = unwrapResponse(response);
    return data?.granted ?? false;
  },

  /**
   * Update feature permissions for a role and page (bulk)
   * Super Admin only
   */
  async updateFeaturePermissions(
    role: string,
    pageKey: string,
    updates: { featureKey: string; action: string; granted: boolean }[]
  ): Promise<void> {
    await apiClient.put(API_ENDPOINTS.PERMISSIONS.UPDATE_FEATURE(role, pageKey), { updates });
  },

  /**
   * Get permission change logs (audit trail)
   * Super Admin only
   */
  async getPermissionLogs(limit: number = 100, offset: number = 0): Promise<PermissionLog[]> {
    const response = await apiClient.get(API_ENDPOINTS.PERMISSIONS.LOGS, {
      params: { limit, offset },
    });
    return unwrapResponse(response) as PermissionLog[];
  },
};

export default permissionService;
